/**
 * AdBreakOverlay - Non-skippable ad break player
 * Displays video ads and billboard images with countdown timer.
 * Features: smooth fade transitions, progress dots, holding screen.
 * Triggered externally via VMix/StreamDeck webhook, rendered on top of the stream.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Timer, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const AdBreakOverlay = ({ broadcastId, onActiveChange }) => {
  // ASN Plus subscribers get ad-free viewing
  const userData = JSON.parse(localStorage.getItem('userData') || 'null');
  const isPlus = userData?.is_plus === true;

  const [active, setActive] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemElapsed, setItemElapsed] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const videoRef = useRef(null);
  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const wasActive = useRef(false);

  const advanceToNext = useCallback(() => {
    if (currentIndex < playlist.length - 1) {
      const currentItem = playlist[currentIndex];
      if (currentItem?.id && currentItem.id !== 'holding_screen') {
        fetch(`${API}/video-ads/track/complete/${currentItem.id}`, { method: 'POST' }).catch(() => {});
      }
      setTransitioning(true);
      setTimeout(() => {
        setCurrentIndex(i => i + 1);
        setItemElapsed(0);
        setTimeout(() => setTransitioning(false), 50);
      }, 400);
    }
  }, [currentIndex, playlist]);

  // Poll for ad break status
  const pollStatus = useCallback(async () => {
    if (!broadcastId || isPlus) return; // ASN Plus: skip ad breaks
    try {
      const res = await fetch(`${API}/ad-breaks/broadcasts/${broadcastId}/ad-break`);
      if (!res.ok) return;
      const data = await res.json();

      if (data.active && !wasActive.current) {
        wasActive.current = true;
        setActive(true);
        setDuration(data.duration);
        setRemaining(data.remaining_seconds);
        setPlaylist(data.playlist || []);
        setCurrentIndex(0);
        setItemElapsed(0);
        setTransitioning(false);
        onActiveChange?.(true);
      } else if (data.active) {
        setRemaining(data.remaining_seconds);
      } else if (!data.active && wasActive.current) {
        wasActive.current = false;
        setActive(false);
        setPlaylist([]);
        setCurrentIndex(0);
        onActiveChange?.(false);
      }
    } catch {}
  }, [broadcastId, onActiveChange]);

  useEffect(() => {
    pollRef.current = setInterval(pollStatus, 2000);
    pollStatus();
    return () => clearInterval(pollRef.current);
  }, [pollStatus]);

  // Item timer - advance through playlist
  useEffect(() => {
    if (!active || playlist.length === 0) return;

    timerRef.current = setInterval(() => {
      setItemElapsed(prev => {
        const currentItem = playlist[currentIndex];
        const itemDuration = currentItem?.duration_seconds || 15;
        if (prev + 1 >= itemDuration) {
          if (currentIndex < playlist.length - 1) {
            advanceToNext();
          }
          return prev + 1;
        }
        return prev + 1;
      });
      setRemaining(r => Math.max(0, r - 1));
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [active, playlist, currentIndex, advanceToNext]);

  // Track view when item changes
  useEffect(() => {
    if (!active || playlist.length === 0) return;
    const item = playlist[currentIndex];
    if (item?.id && item.id !== 'holding_screen') {
      fetch(`${API}/video-ads/track/click/${item.id}`, { method: 'POST' }).catch(() => {});
    }
  }, [currentIndex, active, playlist]);

  // Auto-play video when item changes
  useEffect(() => {
    const v = videoRef.current;
    if (v && active) {
      v.currentTime = 0;
      // Browser may block autoplay with sound; fall back to muted so the ad still plays
      v.play().catch(() => { v.muted = true; v.play().catch(() => {}); });
    }
  }, [currentIndex, active]);

  const handleAdClick = (item) => {
    if (item?.click_url) {
      if (item?.id && item.id !== 'holding_screen') {
        fetch(`${API}/video-ads/track/click/${item.id}`, { method: 'POST' }).catch(() => {});
      }
      window.open(item.click_url, '_blank', 'noopener');
    }
  };

  if (!active) return null;

  // No ads/holding media configured: still cover the broadcast with a text holding screen
  const currentItem = playlist[currentIndex] || { id: 'holding_screen', ad_type: 'holding', duration_seconds: 0 };
  const isHolding = currentItem?.ad_type === 'holding';
  const isVideo = !isHolding && currentItem?.ad_type !== 'billboard' && currentItem?.video_url;
  const isBillboard = !isHolding && (currentItem?.ad_type === 'billboard' || (!currentItem?.video_url && currentItem?.image_url));
  const isHoldingVideo = isHolding && currentItem?.video_url;
  const isHoldingImage = isHolding && !currentItem?.video_url && currentItem?.image_url;
  const itemDuration = currentItem?.duration_seconds || 15;
  const itemProgress = Math.min(100, (itemElapsed / itemDuration) * 100);

  // Count real ads (not holding screen)
  const realAds = playlist.filter(p => p.ad_type !== 'holding');
  const realAdIndex = realAds.findIndex(a => a.id === currentItem?.id);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}:${sec.toString().padStart(2, '0')}` : `0:${sec.toString().padStart(2, '0')}`;
  };

  const resolveUrl = (url) => {
    if (!url) return '';
    return url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };

  return (
    <div
      className="absolute inset-0 z-50 bg-black"
      style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
      onContextMenu={(e) => e.preventDefault()}
      data-testid="ad-break-overlay"
    >
      {/* Ad Content with fade transition */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          opacity: transitioning ? 0 : 1,
          transition: 'opacity 0.4s ease-in-out',
          cursor: currentItem?.click_url ? 'pointer' : 'default'
        }}
        onClick={() => handleAdClick(currentItem)}
      >
        {/* Regular video ad */}
        {isVideo && (
          <video
            ref={videoRef}
            src={resolveUrl(currentItem.video_url)}
            autoPlay
            playsInline
            muted={false}
            className="w-full h-full object-contain bg-black"
            onEnded={() => advanceToNext()}
          />
        )}

        {/* Billboard image ad */}
        {isBillboard && currentItem.image_url && (
          <img
            src={resolveUrl(currentItem.image_url)}
            alt={currentItem.name || 'Ad'}
            className="w-full h-full object-contain"
            draggable={false}
          />
        )}

        {/* Holding screen - video */}
        {isHoldingVideo && (
          <div className="relative w-full h-full flex items-center justify-center">
            <video
              ref={videoRef}
              src={resolveUrl(currentItem.video_url)}
              autoPlay
              playsInline
              loop
              muted
              className="w-full h-full object-contain bg-black"
            />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-black/60 backdrop-blur-md rounded-lg px-8 py-5 text-center">
                <p className="text-white/90 text-lg font-medium tracking-wide">Broadcast will return shortly</p>
              </div>
            </div>
          </div>
        )}

        {/* Holding screen - image */}
        {isHoldingImage && (
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src={resolveUrl(currentItem.image_url)}
              alt="Returning Shortly"
              className="w-full h-full object-contain"
              draggable={false}
            />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-black/60 backdrop-blur-md rounded-lg px-8 py-5 text-center">
                <p className="text-white/90 text-lg font-medium tracking-wide">Broadcast will return shortly</p>
              </div>
            </div>
          </div>
        )}

        {/* Holding screen - no media (text only) */}
        {isHolding && !currentItem?.video_url && !currentItem?.image_url && (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-900 via-black to-zinc-900">
            <div className="text-center">
              <p className="text-white/80 text-2xl font-medium tracking-wide">Broadcast will return shortly</p>
            </div>
          </div>
        )}
      </div>

      {/* Click-through indicator */}
      {currentItem?.click_url && !isHolding && (
        <div className="absolute bottom-4 right-4 flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded text-white/70 text-xs pointer-events-none">
          <ExternalLink className="w-3 h-3" />
          Click to learn more
        </div>
      )}

      {/* Sponsor name */}
      {currentItem?.sponsor_name && !isHolding && (
        <div className="absolute bottom-4 left-4 text-white/40 text-[10px] uppercase tracking-widest pointer-events-none">
          Sponsored by {currentItem.sponsor_name}
        </div>
      )}

      {/* Top bar: countdown + progress dots */}
      <div className="absolute top-0 left-0 right-0 pointer-events-none">
        <div className="bg-gradient-to-b from-black/80 via-black/40 to-transparent px-4 pt-3 pb-8">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded">
              <Timer className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-white text-xs font-bold tracking-wide">
                {isHolding ? 'RETURNING SHORTLY' : 'AD BREAK IN PROGRESS'}
              </span>
              {!isHolding && (
                <span className="text-orange-400 text-xs font-mono font-bold ml-1">{formatTime(remaining)}</span>
              )}
            </div>

            {/* Progress dots */}
            {realAds.length > 1 && (
              <div className="flex items-center gap-1.5 ml-2" data-testid="ad-progress-dots">
                {realAds.map((_, i) => (
                  <div
                    key={i}
                    className={`rounded-full transition-all duration-300 ${
                      i < realAdIndex
                        ? 'w-2 h-2 bg-orange-500'
                        : i === realAdIndex
                        ? 'w-3 h-2 bg-orange-400 rounded-full'
                        : 'w-2 h-2 bg-white/20'
                    }`}
                  />
                ))}
              </div>
            )}

            {!isHolding && (
              <span className="text-white/30 text-[10px] ml-auto">
                {(realAdIndex >= 0 ? realAdIndex : 0) + 1} / {realAds.length}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Item progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10 pointer-events-none">
        <div
          className="h-full bg-orange-500 transition-all duration-1000 ease-linear"
          style={{ width: `${itemProgress}%` }}
          data-testid="ad-progress-bar"
        />
      </div>
    </div>
  );
};

export default AdBreakOverlay;
